import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AyvensQuestion } from '../components/ayvens/AyvensQuestion';
import { KeyRound, Mail, User, Car, Settings, FolderOpen, Sparkles, Briefcase, CheckCircle, CheckCircle2, UserPlus, Building2, Phone, Send, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente Form SMART riusabile (stesso della Vetrina)
const SmartRegistrationForm = ({ onBack, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [giaClienteAyvens, setGiaClienteAyvens] = useState(null);
  const [formData, setFormData] = useState({
    nome_cognome: '',
    telefono: '',
    email: '',
    messaggio: '',
    codice_convenzione: '',
    password: '',
    conferma_password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione campi obbligatori
    if (!formData.nome_cognome || !formData.email || !formData.telefono) {
      toast.error('Compila tutti i campi obbligatori (Nome, Email, Telefono)');
      return;
    }

    // Validazione Password (min 8, match)
    if (!formData.password || formData.password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    if (formData.password !== formData.conferma_password) {
      toast.error('Le password non coincidono');
      return;
    }

    // Validazione Privacy Policy
    if (!privacyAccepted) {
      toast.error('Devi accettare la Privacy Policy per procedere');
      return;
    }
    if (giaClienteAyvens === null) {
      toast.error('Indica se sei già cliente Ayvens / ALD Automotive');
      return;
    }
    
    // Validazione email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/public/richiesta-info`, {
        nome_cognome: formData.nome_cognome,
        telefono: formData.telefono,
        email: formData.email,
        messaggio: formData.messaggio,
        veicolo_interesse: null,
        origine: 'registrazione', // Indica che viene dalla pagina registrazione
        codice_convenzione: (formData.codice_convenzione || '').toUpperCase() || undefined,
        password: formData.password,
        conferma_password: formData.conferma_password,
        accept_privacy: privacyAccepted,
        gia_cliente_ayvens: giaClienteAyvens,
      });
      
      if (response.data.success) {
        // Auto-login: il server ha già impostato il cookie di sessione; full reload
        // per re-inizializzare AuthContext con la sessione appena creata
        if (response.data.access_token) {
          if (response.data.ayvens_message) toast.info(response.data.ayvens_message, { duration: 8000 });
          toast.success('Benvenuto! Ti stiamo portando nella tua area riservata...');
          // Sub-release 2.6: appena registrato → cliente sicuramente ha 0 preventivi → catalogo-offerte
          setTimeout(() => { window.location.href = '/dashboard/catalogo-offerte'; }, response.data.ayvens_message ? 5000 : 500);
          return;
        }
        toast.success(response.data.message || 'Registrazione completata!');
        setSuccess(true);
        if (onSuccess) onSuccess();
      } else {
        toast.error('Si è verificato un errore. Riprova.');
      }
    } catch (error) {
      console.error('Errore invio richiesta:', error);
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error('Verifica i dati inseriti: ' + errorMsg.map(e => e.msg).join(', '));
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Errore durante l\'invio. Verifica i dati e riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="py-6 text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">Richiesta Inviata!</h3>
        <p className="text-slate-600">
          Il tuo account è stato creato. Ora puoi accedere all&apos;area riservata con le credenziali che hai appena scelto.
        </p>
        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-emerald-800 font-medium">
            Un nostro consulente ti contatterà per completare le informazioni necessarie e offrirti le migliori opzioni.
          </p>
        </div>
        <div className="pt-4">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Header con pulsante indietro */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm"
          onClick={onBack}
          className="p-1 h-auto"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h3 className="font-semibold text-slate-800">Registrazione Cliente</h3>
          <p className="text-xs text-slate-500">Inserisci i tuoi dati per essere contattato</p>
        </div>
      </div>

      {/* Form fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="smart-nome">Nome e Cognome / Ragione Sociale *</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="smart-nome"
              data-testid="smart-form-nome"
              value={formData.nome_cognome}
              onChange={(e) => setFormData({...formData, nome_cognome: e.target.value})}
              placeholder="Es. Mario Rossi / Rossi SRL"
              className="pl-10"
              autoComplete="name"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="smart-telefono">Telefono *</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="smart-telefono"
              data-testid="smart-form-telefono"
              value={formData.telefono}
              onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              placeholder="333 1234567"
              className="pl-10"
              autoComplete="tel"
              required
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="smart-email">Email *</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="smart-email"
              type="email"
              data-testid="smart-form-email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="esempio@email.it"
              className="pl-10"
              autoComplete="off"
              required
            />
          </div>
        </div>

        {/* Password + Conferma (min 8 caratteri) — servono per l'auto-login post registrazione */}
        <div className="space-y-2">
          <Label htmlFor="smart-password">
            Password * <span className="text-xs text-slate-400 font-normal">(min 8 caratteri)</span>
          </Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="smart-password"
              type="password"
              data-testid="smart-form-password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="La tua password"
              className="pl-10"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="smart-conferma-password">Conferma Password *</Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              id="smart-conferma-password"
              type="password"
              data-testid="smart-form-conferma-password"
              value={formData.conferma_password}
              onChange={(e) => setFormData({...formData, conferma_password: e.target.value})}
              placeholder="Ripeti la password"
              className="pl-10"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
        </div>

        <AyvensQuestion value={giaClienteAyvens} onChange={setGiaClienteAyvens} testIdPrefix="smart-form-ayvens" />

        {/* Messaggio opzionale */}
        <div className="space-y-2">
          <Label htmlFor="smart-messaggio">Note o richieste particolari</Label>
          <textarea
            id="smart-messaggio"
            data-testid="smart-form-messaggio"
            value={formData.messaggio}
            onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
            className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="Eventuali richieste o domande..."
            rows={2}
          />
        </div>

        {/* Codice Convenzione (opzionale) */}
        <div className="space-y-2">
          <Label htmlFor="smart-codice-conv">Codice Convenzione (opzionale)</Label>
          <Input
            id="smart-codice-conv"
            data-testid="smart-form-codice-convenzione"
            value={formData.codice_convenzione}
            onChange={(e) => setFormData({...formData, codice_convenzione: e.target.value.toUpperCase()})}
            placeholder="Inserisci codice"
          />
        </div>
      </div>

      {/* Privacy Policy */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border">
        <input
          type="checkbox"
          id="smart-privacy"
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
          className="mt-1"
          data-testid="smart-form-privacy"
        />
        <label htmlFor="smart-privacy" className="text-xs text-slate-600 leading-tight">
          Accetto la <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a> e il trattamento dei miei dati personali ai fini della richiesta di informazioni commerciali. *
        </label>
      </div>

      {/* Info box */}
      <div className="text-xs text-slate-500 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="font-medium text-blue-700 mb-1">Cosa succede dopo?</p>
        <p className="text-blue-600">
          Otterrai subito accesso alla tua area riservata con le credenziali che hai scelto. Un consulente ti contatterà per accompagnarti nella scelta.
        </p>
      </div>

      <Button 
        type="submit" 
        className="w-full"
        data-testid="smart-form-submit"
        disabled={submitting}
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio in corso...</>
        ) : (
          <><Send className="w-4 h-4 mr-2" />Invia Richiesta</>
        )}
      </Button>
    </form>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showSmartForm, setShowSmartForm] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userAfter = await login(loginData.email, loginData.password);
      toast.success('Accesso effettuato!');
      // Sub-release 2.6 — Redirect intelligente cliente:
      // se ha 0 preventivi salvati, portalo direttamente al Catalogo Offerte
      const isCliente = userAfter && ['cliente', 'cliente_smart'].includes(userAfter.role);
      if (isCliente) {
        try {
          const prevRes = await axios.get(`${API}/client/preventivi`);
          const count = prevRes.data?.count || 0;
          if (count === 0) {
            navigate('/dashboard/catalogo-offerte');
            return;
          }
        } catch { /* fallback below */ }
        navigate('/dashboard/offerte');
        return;
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <div className="flex flex-1 pb-14">
      {/* Left side - Company info and branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1760713164476-7eb5063b3d07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBzaWRlJTIwcHJvZmlsZSUyMHN0dWRpbyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc2ODQzMTI0NHww&ixlib=rb-4.1.0&q=85)' 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 to-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-center px-12 py-10 w-full">
          {/* Top section - Branding text */}
          <div>
            <p className="text-xl text-slate-200 max-w-md leading-relaxed mb-4">
              Il tuo accesso privilegiato al mondo del Noleggio a Lungo Termine.
            </p>
            
            {/* Features */}
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Car className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <span className="font-medium text-white">Catalogo Multimarca</span>
                  <p className="text-sm text-slate-400">Auto nuove, usate e KM0 sempre aggiornate</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <span className="font-medium text-white">Configuratore Smart</span>
                  <p className="text-sm text-slate-400">Personalizza durata, km e anticipo in pochi istanti</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <span className="font-medium text-white">Area Riservata</span>
                  <p className="text-sm text-slate-400">Gestisci le tue pratiche e i tuoi ordini in totale autonomia</p>
                </div>
              </div>
            </div>

            {/* CTA Vetrina Pubblica */}
            <div className="mt-10 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span className="text-white font-semibold">Non sei ancora registrato?</span>
              </div>
              <p className="text-slate-300 text-sm mb-4">
                Scopri subito le nostre migliori offerte senza impegno!
              </p>
              <Link to="/vetrina">
                <Button 
                  variant="outline" 
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 border-amber-500 hover:border-amber-400 font-semibold text-base py-6"
                >
                  <Car className="w-5 h-5 mr-2" />
                  Scopri le Offerte in Vetrina
                </Button>
              </Link>
              <p className="text-center text-xs text-slate-400 mt-3">
                Senza impegno, prezzi IVA esclusa
              </p>
            </div>

            {/* CTA Lavora con Noi */}
            <div className="mt-6 p-5 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm rounded-2xl border border-emerald-400/30">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="w-5 h-5 text-emerald-400" />
                <span className="text-white font-semibold">Sei un professionista?</span>
              </div>
              <p className="text-slate-300 text-sm mb-3">
                Entra nella rete MAcheNoleggio come agente
              </p>
              <Link to="/lavora-con-noi">
                <Button 
                  variant="outline" 
                  className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border-emerald-400/50 hover:border-emerald-400 font-medium"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Lavora con Noi
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex flex-col justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md mx-auto">
          {/* Desktop logo - centrato sopra il form */}
          <div className="hidden lg:flex lg:flex-col lg:items-center mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-lg">
              <img 
                src="/assets/logos/logo-dark.png?v=2026" 
                alt="MAcheNoleggio" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-slate-500 mt-3 text-center">
              Un servizio <span className="font-semibold text-slate-700">MAAC FINANCE SRL</span>
            </p>
          </div>
          
          {/* Mobile logo and company info */}
          <div className="lg:hidden mb-6">
            <div className="bg-white rounded-2xl p-4 shadow-lg inline-block mb-4 mx-auto flex justify-center w-full">
              <img 
                src="/assets/logos/logo-dark.png?v=2026" 
                alt="MAcheNoleggio" 
                className="h-14 w-auto object-contain"
              />
            </div>
            <p className="text-center text-sm text-slate-500 mb-4">
              Un servizio <span className="font-semibold text-slate-700">MAAC FINANCE SRL</span>
            </p>
            {/* Mobile CTA Vetrina */}
            <Link to="/vetrina" className="block">
              <Button 
                variant="outline" 
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 border-amber-500 hover:border-amber-400 font-semibold py-5"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Scopri le Offerte in Vetrina
              </Button>
            </Link>
            <p className="text-center text-xs text-slate-400 mt-2 mb-3">
              Senza impegno, prezzi IVA esclusa
            </p>
            {/* Mobile CTA Lavora con Noi */}
            <Link to="/lavora-con-noi" className="block">
              <Button 
                variant="outline" 
                className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-300 hover:border-emerald-400 font-medium py-4"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Lavora con Noi
              </Button>
            </Link>
          </div>

          <Card className="border-0 shadow-xl" data-testid="login-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Benvenuto
              </CardTitle>
              <CardDescription className="text-center text-sm leading-relaxed">
                Il nostro catalogo è in continuo aggiornamento. Registrati per essere ricontattato da un professionista e ricevere la tua quotazione riservata.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="nome@esempio.it"
                          className="pl-10 input-focus"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          autoComplete="email"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10 input-focus"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          autoComplete="current-password"
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-scale bg-primary hover:bg-primary/90"
                      disabled={isLoading}
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? 'Accesso in corso...' : 'Accedi'}
                    </Button>
                    
                    {/* Link Password Dimenticata */}
                    <div className="text-center mt-3">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-slate-500 hover:text-primary hover:underline transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Password dimenticata?
                      </Link>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  {showSmartForm ? (
                    /* Form SMART inline - Nessun redirect */
                    <SmartRegistrationForm 
                      onBack={() => setShowSmartForm(false)}
                      onSuccess={() => {}}
                    />
                  ) : (
                    /* Scelta del Ruolo - Cliente vs Agente */
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600 text-center mb-4">
                        Come vuoi registrarti?
                      </p>
                      
                      {/* Opzione Cliente - Ora apre il form SMART inline */}
                      <div 
                        onClick={() => setShowSmartForm(true)}
                        className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 cursor-pointer transition-all group"
                        data-testid="register-cliente-btn"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                            <Building2 className="w-6 h-6 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-800 group-hover:text-amber-700">
                              Sono un Cliente
                            </h3>
                            <p className="text-sm text-slate-500">
                              Cerco un preventivo per noleggio auto
                            </p>
                          </div>
                          <Sparkles className="w-5 h-5 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      {/* Opzione Agente */}
                      <div 
                        onClick={() => navigate('/lavora-con-noi')}
                        className="p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 cursor-pointer transition-all group"
                        data-testid="register-agente-btn"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                            <Briefcase className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-800 group-hover:text-emerald-700">
                              Voglio collaborare (Agente)
                            </h3>
                            <p className="text-sm text-slate-500">
                              Candidati come agente MAcheNoleggio
                            </p>
                          </div>
                          <UserPlus className="w-5 h-5 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      {/* Nota informativa */}
                      <div className="mt-4 p-3 bg-slate-100 rounded-lg text-xs text-slate-500 text-center">
                        <CheckCircle className="w-4 h-4 inline-block mr-1 text-slate-400" />
                        Tutte le registrazioni richiedono approvazione dell&apos;Admin
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Footer - Dati Societari */}
      <footer className="absolute bottom-0 left-0 right-0 border-t border-slate-200 bg-slate-50/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[11px] text-slate-500">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium text-slate-600">MAAC FINANCE SRL</span>
              <span className="text-slate-400">|</span>
              <span>Titolari: L. Manenti e R. Achilli</span>
              <span className="text-slate-400">|</span>
              <span>Sede Legale: Via di Casalotti 39, Roma</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="hidden lg:inline">Sedi Operative: Roma (Via Carrù 13c), Velletri (Viale R. Margherita 7), Civitavecchia (Via V. Strambi 12), Latina (Via Piave 836)</span>
              <span className="lg:hidden">4 Sedi Operative nel Lazio</span>
              <span className="text-slate-400">|</span>
              <span>P.IVA: IT10540961009</span>
              <span className="text-slate-400">|</span>
              <a 
                href="https://www.maacfinancesrl.it" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                www.maacfinancesrl.it
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
